import { sendInteractionEvent as originalSendInteractionEvent } from "@tql/web-metrics";

import { environmentConfig } from "./environment-config.ts";

export type QuoteWorkflow = "create_quote" | "resume_quote";

/**
 * Determines if GA events should be logged to console.
 * Enabled for local, dev, stage environments.
 * Disabled for prod environment.
 */
export function shouldLogGaEvents(): boolean {
	const stage = environmentConfig.environmentName;
	return stage !== "prod";
}

/**
 * Wrapper for @tql/web-metrics sendInteractionEvent that logs to console
 * in non-prod environments for debugging purposes.
 *
 * @param args - The interaction event parameters forwarded to @tql/web-metrics
 */
export function sendInteractionEvent(
	...args: Parameters<typeof originalSendInteractionEvent>
): void {
	const [payload] = args;
	if (shouldLogGaEvents()) {
		console.log("[GA Event]", payload);
		console.log("[Data Layer]", globalThis.dataLayer);
	}
	originalSendInteractionEvent(...args);
}

/**
 * Pushes the user's employee ID to the Google Analytics dataLayer
 * so that all subsequent GA events are associated with this user.
 * Should be called once after the user is authenticated.
 *
 * @param employeeId - The employee ID from the MSAL ID token claims
 */
export function enrichAnalyticsForInternalUser(employeeId: string): void {
	if (globalThis.dataLayer) {
		globalThis.dataLayer.push({
			is_internal_user: true,
			employee_id: employeeId,
		});
	}
}

/**
 * Pushes the user's user GUID and all associated customer Ids to the Google Analytics dataLayer
 * so that all subsequent GA events are associated with this user.
 * Should be called once after the user is authenticated.
 *
 * @param userGuid - The user GUID from the MSAL ID token claims
 * @param customerIds - The customer IDs from the MSAL ID token claims
 */
export function enrichAnalyticsForExternalUser(
	userGuid: string,
	customerIds: string[],
): void {
	if (globalThis.dataLayer) {
		globalThis.dataLayer.push({
			is_internal_user: false,
			user_guid: userGuid,
			customer_ids: customerIds,
		});
	}
}

/**
 * Pushes the active customer profile id to the Google Analytics dataLayer so that
 * subsequent external-user GA events are attributed to the profile the user is acting under.
 * Callers must only invoke this once the profile is resolved; do not pass a sentinel during
 * bootstrap, or startup events will be misattributed to a non-existent profile.
 *
 * @param customerId - The resolved active customer profile id
 */
export function enrichAnalyticsWithActiveCustomer(customerId: number): void {
	if (globalThis.dataLayer) {
		globalThis.dataLayer.push({
			active_customer_id: customerId,
		});
	}
}

/**
 * Clears the Google Analytics dataLayer.
 * Should be called when a user logs out to ensure no user-specific
 * data persists for subsequent sessions.
 */
export function clearAnalyticsDataLayer(): void {
	if (globalThis.dataLayer) {
		globalThis.dataLayer.length = 0;
	}
}

/**
 * Sends a GA event when the user finishes creating a quote by clicking "Get Quote".
 *
 * @param quoteId - The ID of the quote
 * @param workflow - Whether this is a new quote or a resumed quote
 */
export function sendFinishCreateQuoteEvent(
	quoteId: string,
	workflow: QuoteWorkflow,
): void {
	sendInteractionEvent({
		event: "create_quote_interaction",
		cta_text: "Get Quote",
		interaction: "finish_create_quote",
		page_section: "create_quote",
		sub_section: "footer",
		selection: quoteId,
		selection_2: "-",
		workflow,
	} as Parameters<typeof originalSendInteractionEvent>[0]);
}

/**
 * Sends a GA event when the user successfully books a carrier.
 *
 * @param quoteId - The ID of the quote
 * @param carrierId - The ID of the booked carrier
 * @param workflow - Whether this is a new quote or a resumed quote
 */
export function sendBookCarrierEvent(
	quoteId: string,
	carrierId: string,
	workflow: QuoteWorkflow,
): void {
	sendInteractionEvent({
		event: "create_quote_interaction",
		cta_text: "Book",
		interaction: "book_carrier",
		page_section: "get_quote",
		sub_section: "carrier_options",
		selection: quoteId,
		selection_2: carrierId,
		workflow,
	} as Parameters<typeof originalSendInteractionEvent>[0]);
}

/**
 * Sends a GA event when the user successfully tenders a quote.
 *
 * @param quoteId - The ID of the quote
 * @param workflow - Whether this is a new quote or a resumed quote
 */
export function sendTenderQuoteEvent(
	quoteId: string,
	workflow: QuoteWorkflow,
): void {
	sendInteractionEvent({
		event: "create_quote_interaction",
		cta_text: "Tender",
		interaction: "tender_quote",
		page_section: "shipment_details",
		sub_section: "footer",
		selection: quoteId,
		selection_2: "-",
		workflow,
	} as Parameters<typeof originalSendInteractionEvent>[0]);
}

/**
 * Sends a GA event when the user clicks a quote ID on the quote board to resume editing.
 *
 * @param quoteId - The ID of the quote
 */
export function sendQuoteBoardEditQuoteIdEvent(quoteId: string): void {
	sendInteractionEvent({
		event: "quote_board_interaction",
		cta_text: "-",
		interaction: "edit_quote_id",
		page_section: "quote_board",
		selection: quoteId,
	} as Parameters<typeof originalSendInteractionEvent>[0]);
}

/**
 * Sends a GA event when the user clicks a status/step button on the quote board to resume editing.
 *
 * @param quoteId - The ID of the quote
 * @param ctaText - The exact button text displayed (e.g. "Select a Carrier", "Ready to Tender")
 */
export function sendQuoteBoardEditQuoteStepEvent(
	quoteId: string,
	ctaText: string,
): void {
	sendInteractionEvent({
		event: "quote_board_interaction",
		cta_text: ctaText,
		interaction: "edit_quote_step",
		page_section: "quote_board",
		selection: quoteId,
	} as Parameters<typeof originalSendInteractionEvent>[0]);
}

/**
 * Sends a GA event for a "Get Quote" button click in the Create FTL Quote webpage.
 *
 * @param quoteId - The ID of the quote
 */
export function sendFtlQuoteGetQuoteEvent(quoteId: string): void {
	sendInteractionEvent({
		event: "create_quote_interaction",
		trigger: "User gets quote info after adding details",
		cta_text: "Get Quote",
		interaction: "create_quote",
		page_section: "quote_details",
		selection: quoteId,
	} as Parameters<typeof originalSendInteractionEvent>[0]);
}

/**
 * Sends a GA event for a "TenderShipment" button click in the FTL Quote webpage.
 *
 * @param quoteId - The ID of the quote
 */
export function sendFtlQuoteTenderShipmentEvent(quoteId: string): void {
	sendInteractionEvent({
		event: "create_quote_interaction",
		trigger: "User tenders FTL quote",
		cta_text: "Tender Shipment",
		interaction: "tender_quote",
		page_section: "rate_tender",
		selection: quoteId,
	} as Parameters<typeof originalSendInteractionEvent>[0]);
}

/**
 * Sends a GA event for a "Revise" button click in the FTL Quote webpage.
 */
export function sendFtlQuoteReviseQuoteEvent(): void {
	sendInteractionEvent({
		event: "create_quote_interaction",
		trigger: "User returns to revise quote details",
		cta_text: "Revise",
		interaction: "revise_quote",
		page_section: "rate_tender",
	} as Parameters<typeof originalSendInteractionEvent>[0]);
}

/**
 * Sends a GA event for a "TenderShipment" button click in the Tender FTL Shipment webpage.
 *
 * @param quoteId - The ID of the quote
 */
export function sendTenderFtlShipmentTenderEvent(quoteId: string): void {
	sendInteractionEvent({
		event: "create_quote_interaction",
		trigger: "User tenders FTL shipment",
		cta_text: "Tender Shipment",
		interaction: "tender_quote",
		page_section: "tender_ftl",
		selection: quoteId,
	} as Parameters<typeof originalSendInteractionEvent>[0]);
}

/**
 * Pushes a Google Ads conversion event to the GTM dataLayer for a successful
 * pre-pay LTL tender. Mirrors the legacy `GoogleTagManager.sendGoogleAdsConversionEvent`.
 *
 * @param customerRate - Booked customer rate, as a string (matches legacy contract)
 * @param transactionId - Unique transaction identifier (legacy uses `quote.poNumber.toString()`)
 */
export function sendGoogleAdsConversionEvent(
	customerRate: string,
	transactionId: string,
): void {
	const payload = {
		event: "google_ads_conversion",
		conversion_value: customerRate,
		transaction_id: transactionId,
	};
	if (shouldLogGaEvents()) {
		console.log("[GA Event]", payload);
	}
	if (globalThis.dataLayer) {
		globalThis.dataLayer.push(payload);
	}
}
